// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import type { ReactNode } from 'react';
import React from 'react';
import type { CSSObject } from '@emotion/serialize';
import { Box } from '@mui/material';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import type { ITheme } from '../../../component_library/styles/theme';
import { MenuToopltipWithLinks } from '../../../component_library/components/Tooltip/MenuToopltipWithLinks';

export const NAV_CONTEXT_MENU_BUTTON_BASE_STYLE: CSSObject = {
  display: 'flex',
  flexDirection: 'row',
  columnGap: '8px',
  alignItems: 'center',
  width: 'fit-content',
  outline: 'none',
};

interface INavContextMenu extends IPropsWithTestId {
  useDarkFontColor?: boolean;
  titleProps: IPropsWithTestId & {
    content: ReactNode;
  };
  renderTooltipContent: React.FC<{
    handleCloseTooltipWithNoTransition: () => void;
  }>;
  menuOpen: boolean;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  theme: ITheme;
}

export const NavContextMenu: React.FC<INavContextMenu> = ({
  useDarkFontColor,
  titleProps,
  renderTooltipContent,
  menuOpen,
  setMenuOpen,
  theme,
  dataElementId,
}) => {
  const fontColor = useDarkFontColor
    ? theme.colors.secondary
    : theme.colors.grayscale.white;

  const titleStyles: CSSObject = {
    color: fontColor,
    // A bug in iOS < 16 makes 'unset: all' not work if you try setting
    // properties afterwards which have Webkit equivalent properties.
    // https://stackoverflow.com/q/56248747/5602521
    // The solution is to specify all webkit properties in addition
    // to the regular property names
    WebkitTextFillColor: fontColor,
  };

  const tooltipStyles = {
    popper: {
      sx: {
        '& .MuiTooltip-tooltip': {
          backgroundColor: theme.colors.grayscale.white,
          boxShadow: '0px 2px 3px 0px #00000026',
          padding: 0,
          borderRadius: '10px',
        },
      },
    },
    arrow: {
      sx: {
        color: 'white',
        fontSize: '30px',
      },
    },
  };

  return (
    <MenuToopltipWithLinks
      dataElementId={dataElementId}
      titleProps={{
        ...titleProps,
        additionalStyles: {
          ...NAV_CONTEXT_MENU_BUTTON_BASE_STYLE,
          ...titleStyles,
        },
        additionalIconStyles: { fontSize: '10px' },
      }}
      renderTooltipContent={(handleCloseTooltipWithNoTransition) => (
        <Box sx={{ padding: '24px' }}>
          {renderTooltipContent(handleCloseTooltipWithNoTransition)}
        </Box>
      )}
      openTooltip={menuOpen}
      setOpenTooltip={setMenuOpen}
      theme={theme}
      componentsProps={tooltipStyles}
      isInHeader
    />
  );
};
